// ═══════════════════════════════════════════════════════════════════════════
// urlSegura — la URL sin sus secretos, en un módulo PURO
// ═══════════════════════════════════════════════════════════════════════════
//
// Vive aparte de `logger.js` (report zVQxpF567HxLjVsYhysR) porque el backend la
// necesita igual: el server persiste la URL que manda el cliente, y un bundle
// viejo la manda cruda. `functions/lib/urlSegura.js` es su espejo CJS y
// `functions/lib/urlSegura.test.js` carga las dos puntas y las compara; por eso
// este archivo no importa nada (el logger arrastra firebase y no se puede cargar
// desde `functions/`). `logger.js` la re-exporta: sus importadores no cambian.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * La URL sin sus secretos — lo que se puede escribir en `clientLogs`.
 *
 * POR QUÉ EXISTE. `clientLogs` la lee TODO consultor (`firestore.rules`,
 * `allow read: if isConsultor()`) y se guarda 30 días. Hasta el 20-sep-2026
 * `_persistError` escribía `window.location.href` CRUDO —el único campo de la
 * entrada que no pasaba por `sanitizeString`— así que un crash de render en
 * cualquiera de las pantallas públicas que llevan una credencial en la URL
 * depositaba esa credencial entera, al lado del `uid` y el `email`:
 * `/setup-password?token=…`, `/conectar/:token`, `/p/caso/:token`, y la que
 * viene, `/instalar?t=…`. El servidor cuida ese mismo dato hasta truncarlo a 8
 * caracteres (`oauth/leerTicketInstalacion.js`: "el id del ticket NO se loguea
 * entero: es la credencial del camino") y el front lo escribía completo.
 *
 * QUÉ CONSERVA. Origen y path —que es lo que sirve para depurar: dice QUÉ
 * pantalla explotó—, los NOMBRES de los parámetros (no sus valores) y si había
 * fragmento. Los valores no se enmascaran con `sanitizeString`: un ticket de 64
 * hex quedaría reconocible por sus extremos, y un token corto no se enmascara
 * en absoluto. Se van enteros.
 *
 * EL PATH TAMBIÉN (report mv9ZeJh5fJ7bqTUA6eIr, 28-oct-2026). Hay rutas que
 * llevan la credencial en un SEGMENTO, no en el query: las de
 * `RUTAS_CON_CREDENCIAL`. Ese segmento se reemplaza por `…` y el resto del
 * path queda, así se sigue sabiendo qué pantalla explotó. La lista vive acá
 * porque la plataforma no lee los módulos; lo que impide que una ruta nueva
 * con credencial quede afuera es `logger.urlSegura.test.js`, que cruza esta
 * lista con todas las rutas públicas del registry.
 */
export function urlSegura(href) {
  if (typeof href !== 'string' || href === '') return null
  let u
  try {
    u = new URL(href)
  } catch {
    return '(url no parseable)'
  }
  const claves = [...new URLSearchParams(u.search).keys()]
  const query = claves.length ? `?${claves.map((k) => `${k}=…`).join('&')}` : ''
  const frag = u.hash ? '#…' : ''
  return `${u.origin}${pathSeguro(u.pathname)}${query}${frag}`
}

/**
 * Las rutas de la SPA (patrones de react-router, sin `/` inicial) que llevan
 * una credencial en un segmento. Cada `:param` de estos patrones se tira.
 */
export const RUTAS_CON_CREDENCIAL = Object.freeze([
  'conectar/:token',
  'p/caso/:token',
  'eventos/reclamar/:token',
  'kit/:token',
])

const PATRONES_CON_CREDENCIAL = RUTAS_CON_CREDENCIAL.map((r) => r.split('/'))

/** El segmento como lo compara react-router: decodificado y sin distinguir mayúsculas. */
function segmentoComparable(seg) {
  try { return decodeURIComponent(seg).toLowerCase() } catch { return seg.toLowerCase() }
}

/**
 * El path con los segmentos de credencial reemplazados por `…`. Matchea por
 * prefijo: lo que venga después del patrón (un `/` final, un segmento de más)
 * no lo salva, porque lo que importa es dónde cae el token. Las barras
 * repetidas se colapsan antes, para que `//conectar//x` no esquive el patrón.
 */
function pathSeguro(pathname) {
  const segs = pathname.replace(/\/{2,}/g, '/').split('/').slice(1)
  const comparables = segs.map(segmentoComparable)
  for (const patron of PATRONES_CON_CREDENCIAL) {
    if (segs.length < patron.length) continue
    const coincide = patron.every((p, i) => p.startsWith(':') || p === comparables[i])
    if (!coincide) continue
    return `/${segs.map((s, i) => (i < patron.length && patron[i].startsWith(':') ? '…' : s)).join('/')}`
  }
  return pathname
}
